import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import ThemeToggle from "@/components/site/ThemeToggle";

const NAV_LINKS = [
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Coverage", id: "coverage" },
  { label: "Careers", id: "careers" },
  { label: "Contact", id: "contact" },
];

const TafraLogo = ({ size = 36, withGlow = true }) => (
  <span
    className={`relative inline-flex items-center justify-center ${
      withGlow ? "logo-scan" : ""
    }`}
    style={{ width: size, height: size }}
  >
    {withGlow && (
      <span
        aria-hidden
        className="absolute inset-[-35%] animate-breathe pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, var(--accent-blue-glow), transparent 60%)",
          filter: "blur(6px)",
        }}
      />
    )}
    <img
      src="/tafra-icon.png"
      alt="TAFRA Energy logo"
      width={size}
      height={size}
      style={{ filter: "var(--logo-filter)", objectFit: "contain" }}
      className="relative z-10"
    />
  </span>
);

const Header = ({ onOpenAssessment }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl" : ""
      }`}
      style={{
        background: scrolled ? "var(--surface-glass)" : "transparent",
        borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
      }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          data-testid="logo-button"
        >
          <TafraLogo size={40} />
          <div className="flex flex-col leading-none text-left">
            <span
              className="font-display text-[15px] tracking-[0.28em] font-medium"
              style={{ color: "var(--text)" }}
            >
              TAFRA
            </span>
            <span
              className="font-display text-[10px] tracking-[0.4em] mt-1"
              style={{ color: "var(--accent-blue)" }}
            >
              ENERGY
            </span>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm tracking-wide transition-colors duration-300 relative group"
              style={{ color: "var(--text-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent-blue)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
            >
              {l.label}
              <span
                className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-all duration-500"
                style={{ background: "var(--accent-blue)" }}
              />
            </button>
          ))}
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="text-sm tracking-wide transition-colors duration-300"
            style={{ color: "var(--text-faint)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent-crimson-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-faint)")}
          >
            Admin
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <button
            onClick={onOpenAssessment}
            data-testid="header-assessment-btn"
            className="relative px-5 py-2.5 text-sm btn-sharp overflow-hidden group transition-all duration-300"
            style={{
              border: "1px solid var(--accent-crimson)",
              background: "var(--accent-crimson-soft)",
              color: "var(--text)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--accent-crimson)";
              e.currentTarget.style.color = "#FFFFFF";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--accent-crimson-soft)";
              e.currentTarget.style.color = "var(--text)";
            }}
          >
            Request Assessment
          </button>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <ThemeToggle />
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-10 h-10 flex items-center justify-center btn-sharp"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden backdrop-blur-xl"
            style={{
              background: "var(--surface-glass)",
              borderTop: "1px solid var(--border)",
            }}
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="text-left text-base transition-colors"
                  style={{ color: "var(--text)" }}
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-admin"
                className="text-left text-base"
                style={{ color: "var(--text-muted)" }}
              >
                Admin
              </Link>
              <button
                onClick={() => {
                  setOpen(false);
                  onOpenAssessment();
                }}
                data-testid="mobile-assessment-btn"
                className="mt-2 px-5 py-3 text-sm btn-sharp"
                style={{
                  border: "1px solid var(--accent-crimson)",
                  background: "var(--accent-crimson-soft)",
                  color: "var(--text)",
                }}
              >
                Request Assessment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export { TafraLogo };
export default Header;
